import { useState } from 'react';
import styles from './Contact.module.css';
import { SectionWrapper } from '../components/shared/SectionWrapper';
import { Chip } from '../components/shared/Chip';
import { ScriptText } from '../components/shared/ScriptText';
import { Button } from '../components/shared/Button';
import { trackEvent, WEB_CONTACT_SUBMIT } from '../lib/analytics';
import { useSEO } from '../hooks/useSEO';

export function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  useSEO({
    title: 'Contact Us',
    description: 'Get in touch with the LuvLust team. Questions, feedback, or support — we\'d love to hear from you.',
    keywords: 'contact luvlust, luvlust support, couples app help',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void trackEvent(WEB_CONTACT_SUBMIT);
    const subject = encodeURIComponent(`LuvLust Contact: ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:starlitapps.dev@gmail.com?subject=${subject}&body=${body}`;
    setSent(true);
  }

  return (
    <>
      <SectionWrapper className={styles.hero}>
        <div className={styles.heroContent}>
          <Chip>Get in Touch</Chip>
          <h1 className={`display ${styles.h1}`}>
            We'd love to<br /><ScriptText rotate={-2}>hear from you.</ScriptText>
          </h1>
          <p className={styles.sub}>
            Questions, feedback, partnership inquiries, or just want to say hi — drop us a message.
          </p>
        </div>
      </SectionWrapper>

      <SectionWrapper>
        <div className={styles.grid}>
          <div className={styles.info}>
            <h2 className={`display ${styles.h2}`}>Other ways to reach us</h2>

            <div className={styles.contactCard}>
              <h3>Email</h3>
              <a href="mailto:starlitapps.dev@gmail.com" className={styles.emailLink}>
                starlitapps.dev@gmail.com
              </a>
            </div>

            <div className={styles.contactCard}>
              <h3>Response Time</h3>
              <p>We typically respond within 24-48 hours on business days.</p>
            </div>

            <div className={styles.contactCard}>
              <h3>For App Issues</h3>
              <p>If you're having trouble with the app, please include your device model, OS version, and a description of the issue.</p>
            </div>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            {sent ? (
              <div className={styles.success}>
                <span className={styles.successIcon}>✓</span>
                <h3>Email client opened!</h3>
                <p>Complete sending the email in your email app. We'll get back to you within 48 hours.</p>
              </div>
            ) : (
              <>
                <div className={styles.field}>
                  <label htmlFor="name">Your Name</label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane & John"
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us what's on your mind..."
                  />
                </div>
                <Button variant="primary" type="submit">Send Message</Button>
              </>
            )}
          </form>
        </div>
      </SectionWrapper>
    </>
  );
}
